import { useTranslation } from 'react-i18next';
import { Timer } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatHms, useCountdown } from './round-clock';

/** Under this, the clock turns red: the last minutes are the ones that matter. */
const DEFAULT_URGENT_SECONDS = 60;

interface RoundCountdownProps {
  /**
   * Seconds left when the round was fetched. Counted down from here rather than
   * from `ends_at`, so a device with a wrong clock still shows the right number
   * of seconds — what matters to a climber is how long they have, not what time
   * the server thinks it is.
   */
  secondsRemaining: number;
  /** Called once, when the clock reaches zero. */
  onExpire?: () => void;
  /**
   * Small text before the clock, saying what it is counting. Hidden once it
   * reaches zero, where `expiredLabel` takes over.
   */
  label?: string;
  /** What it says at zero. Defaults to a round's "time up". */
  expiredLabel?: string;
  /**
   * Below this many seconds the clock turns red. **Zero never turns red**,
   * which is what a countdown to a *start* wants: nothing is running out.
   */
  urgentSeconds?: number;
  className?: string;
}

/**
 * A round's clock, in h:m:s — the time left in one that is running, or the time
 * until one that has not started begins.
 *
 * It ticks locally from the figure the server sent, and every fetch of the round
 * resets it — so drift never accumulates past one poll, and a page left open
 * still lands on zero at the same moment everybody else does. **It never goes
 * below zero** in either direction.
 *
 * Reaching zero is not just a display: `onExpire` is how the page learns the
 * round is over and stops offering to record anything — or, counting the other
 * way, that it is time to go and look whether the round has begun.
 */
export default function RoundCountdown({
  secondsRemaining,
  onExpire,
  label,
  expiredLabel,
  urgentSeconds = DEFAULT_URGENT_SECONDS,
  className,
}: RoundCountdownProps) {
  const { t } = useTranslation();
  const remaining = useCountdown(secondsRemaining, onExpire);

  const expired = remaining <= 0;
  const urgent = !expired && remaining <= urgentSeconds;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg px-3 py-2 font-mono text-2xl font-bold tabular-nums',
        expired && 'bg-muted text-muted-foreground',
        urgent && 'bg-destructive/10 text-destructive',
        !expired && !urgent && 'bg-muted',
        className
      )}
      role="timer"
      aria-live={urgent ? 'assertive' : 'off'}
      data-testid="round-countdown"
    >
      <Timer className={cn('size-5 shrink-0', urgent && 'animate-pulse')} aria-hidden />
      {label && !expired && <span className="text-sm font-normal">{label}</span>}
      <span>{formatHms(remaining)}</span>
      {expired && (
        <span className="text-sm font-normal">
          {expiredLabel ?? t('competitionRounds.timeUp')}
        </span>
      )}
    </div>
  );
}
